/**
 * V3 智能拆书页面
 * 
 * 流程：上传小说 → 分析提取规则 → 生成大纲
 * 使用 MiniMax M2.5 模型
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload as UploadIcon,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock3,
  Sparkles,
  FileText,
  BookOpen,
  Wand2,
  RefreshCw,
  BarChart3,
  Users,
  Globe2,
  Heart,
  MapPinned,
  Swords,
  PenTool,
  Target,
  TrendingUp,
  Package,
  Route,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { v3UploadBook, v3UploadBookChunked, v3StartAnalyzeTask, v3RunAnalyzeTask, v3GetAnalyzeTask, v3DeepAnalyze, v3GetProgress, v3GetSummary, v3StartGenerationTask, v3RunGenerationTask, v3GetGenerationTask, type ThreeTierOutlineResponse } from '../lib/api';
import {
  DEFAULT_PROMPT_PROFILES,
  getDefaultPromptProfileById,
  loadActivePromptProfileId,
  loadPromptProfiles,
  saveActivePromptProfileId,
  savePromptProfiles,
  type PromptProfile,
} from '../lib/promptProfiles';
import type { ChapterPlanMode, PromptConfig, RuleObservationReport, RuleSet } from '../../../shared/types';

// 流程阶段
type Stage = 'upload' | 'uploading' | 'uploaded' | 'analyzing' | 'rules' | 'generating' | 'result' | 'deep-analyzing' | 'deep-result' | 'three-tier-generating' | 'three-tier-result';

const CHUNKED_UPLOAD_THRESHOLD = 8 * 1024 * 1024;
const ANALYZE_TASK_RECOVERY_COOLDOWN_MS = 30_000;
const MAX_SOURCE_CHAPTER_PLAN_COUNT = 120;

function shouldFallbackToChunkedUpload(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return [
    '被中断',
    '超时',
    '网络',
    'aborted',
    'failed to fetch',
    'fetch is aborted',
  ].some((keyword) => message.includes(keyword));
}

function getProgressStepIndex(stage: Stage): number {
  if (stage === 'rules' || stage === 'generating' || stage === 'three-tier-generating') {
    return 1;
  }

  if (stage === 'result' || stage === 'deep-analyzing' || stage === 'deep-result' || stage === 'three-tier-result') {
    return 2;
  }

  return 0;
}

function getThreeTierPlanModeLabel(mode?: ChapterPlanMode) {
  return mode === 'source-book' ? '按原书章节拆解' : '按目标字数重规划';
}

function getThreeTierPlanSummary(
  planningMeta: ThreeTierResult['planningMeta'] | undefined,
  actualChapterCount: number
) {
  const plannedChapterCount = planningMeta?.plannedChapterCount || actualChapterCount;
  const sourceChapterCount = planningMeta?.sourceChapterCount;

  if (planningMeta?.chapterPlanMode === 'source-book' && typeof sourceChapterCount === 'number' && sourceChapterCount > 0) {
    return `这次直接跟着原书真实 ${sourceChapterCount} 章逐章展开，不是重新规划的新书结构。`;
  }

  if (planningMeta?.chapterPlanMode === 'target-words' && typeof sourceChapterCount === 'number' && sourceChapterCount > 0) {
    return `这次没有还原原书 ${sourceChapterCount} 章，而是按目标篇幅重新规划成 ${plannedChapterCount} 章的新书结构。`;
  }

  if (planningMeta?.chapterPlanMode === 'target-words') {
    return '这次章数是按目标篇幅重新规划出来的，不能当成原书真实章节数。';
  }

  return `这次实际生成的是 ${plannedChapterCount} 章结构。`;
}

interface BookInfo {
  bookId: string;
  title: string;
  chapters: Array<{
    index: number;
    title: string;
    wordCount: number;
  }>;
  metadata: {
    totalChapters: number;
    totalWords: number;
    encoding: string;
  };
}

interface RulesInfo {
  ruleSetId: string;
  status: string;
  rules: RuleSet;
  contextMode?: 'chapter_yaml' | 'raw_text';
  contextStats?: {
    sampledTextChapters: number;
    storedStructuredChapters: number;
    sampledStructuredChapters: number;
  };
}

interface GeneratedResult {
  outlineId: string;
  outline: {
    title: string;
    genre: string;
    synopsis: string;
    chapters: Array<{
      index: number;
      title: string;
      summary: string;
      wordCount: number;
      keyPoints: string[];
    }>;
  };
  goldenChapters: {
    chapter1: string;
    chapter2: string;
    chapter3: string;
  };
};

// 使用从 api.ts 导入的类型作为 ThreeTierResult
type ThreeTierResult = ThreeTierOutlineResponse;

type RuleSectionKey =
  | 'rhythm'
  | 'shuangdian'
  | 'character'
  | 'worldview'
  | 'cheats'
  | 'romance'
  | 'dungeon'
  | 'conflict'
  | 'style'
  | 'wordCount';

interface ReportMetric {
  label: string;
  value: string;
  ratio?: number;
}

interface RuleSectionPresentation {
  headline: string;
  metrics: ReportMetric[];
  bullets: string[];
}

interface RuleSectionConfig {
  title: string;
  description: string;
  icon: LucideIcon;
  theme: string;
  panelClass: string;
  chipClass: string;
  metricClass: string;
  extract: (section: any) => RuleSectionPresentation;
}

interface ObservationGroup {
  key: string;
  title: string;
  values: string[];
}

type PromptFieldKey = keyof PromptConfig;

type PromptFieldConfigItem = {
  key: PromptFieldKey;
  label: string;
  placeholder: string;
  stage: 'analysis' | 'generation';
};

type PromptSuggestion = {
  key: PromptFieldKey;
  title: string;
  reason: string;
  content: string;
};

interface DeepSummary {
  totalChapters: number;
  protagonistArc: Array<{
    chapter: number;
    realm: string;
    mental: string;
    gains: string[];
  }>;
  characters: Array<{
    name: string;
    roleType?: string;
    role?: string;
    firstAppearance?: number;
  }>;
  items: Array<{
    name: string;
    type?: string;
    rarity?: string;
    chapterAppeared?: number;
    owner?: string;
  }>;
  locations: Array<{
    name: string;
    type: string;
    chapterUnlocked: number;
    description?: string;
    importance: string;
    isDungeon: boolean;
    cleared: boolean;
  }>;
  foreshadowing: {
    total: number;
    planted: number;
    harvested: number;
    details: Array<{
      chapter?: number;
      chapterPlanted?: number;
      chapterHarvested?: number;
      description?: string;
      content?: string;
      status?: string;
      importance?: string;
      category?: string;
    }>;
  };
  plotProgress: Array<{
    chapter: number;
    main: string;
    romance: string;
    revenge: string;
  }>;
}

const PROMPT_FIELD_CONFIG: PromptFieldConfigItem[] = [
  {
    key: 'ruleObservationExtra',
    label: '拆书观察补充',
    placeholder: '例如：优先抽取每章目标、阻力、结果、动作链和关系变化，再总结题材模式。',
    stage: 'analysis',
  },
  {
    key: 'ruleSynthesisExtra',
    label: '规则提炼补充',
    placeholder: '例如：把目标->阻力->动作->结果->悬念压成可复用写作公式，不要只写题材标签。',
    stage: 'analysis',
  },
  {
    key: 'standardOutlineExtra',
    label: '标准大纲补充',
    placeholder: '例如：标准大纲要更强调角色关系和章节递进，不要流水账。',
    stage: 'generation',
  },
  {
    key: 'goldenChapterExtra',
    label: '示范正文补充',
    placeholder: '例如：正文要更口语化，前三段就进入冲突现场，少解释多动作。',
    stage: 'generation',
  },
  {
    key: 'threeTierTotalExtra',
    label: '三层级总纲补充',
    placeholder: '例如：总纲要写清每一阶段的外部对手、升级资源和阶段高潮。',
    stage: 'generation',
  },
  {
    key: 'threeTierVolumeExtra',
    label: '卷纲补充',
    placeholder: '例如：每卷必须有阶段目标、卷尾钩子和资源争夺，不要重复句式。',
    stage: 'generation',
  },
  {
    key: 'threeTierChapterExtra',
    label: '章纲补充',
    placeholder: '例如：每章必须写出具体事件、冲突升级和钩子，不能只写成长概述。',
    stage: 'generation',
  },
];

const RULE_SECTION_ORDER: RuleSectionKey[] = [
  'rhythm',
  'shuangdian',
  'character',
  'worldview',
  'cheats',
  'romance',
  'dungeon',
  'conflict',
  'style',
  'wordCount',
];

function pickStrings(value: unknown, limit = 4): string[] {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter(Boolean)
      .slice(0, limit);
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
}

function formatPercentValue(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '未识别';
  }

  return `${Math.round(value <= 1 ? value * 100 : value)}%`;
}

function normalizeRatio(value: unknown): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }

  const percent = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

function formatCompactNumber(num: number): string {
  if (num >= 100000000) {
    return `${(num / 100000000).toFixed(1)}亿`;
  }
  if (num >= 10000) {
    return `${(num / 10000).toFixed(1)}万`;
  }
  return num.toLocaleString();
}

function buildRuleDigest(rules: any): string {
  const rhythmPattern = rules?.rhythm?.rhythmPattern || '节奏公式待判定';
  const shuangdianTypes = pickStrings(rules?.shuangdian?.types, 2).join('、') || '多类型爽点';
  const coreConflict = rules?.conflict?.coreConflict || '核心冲突待提炼';
  const cheat = rules?.cheats?.mainCheatType || '金手指待识别';
  const romance = rules?.romance?.loveInterestConfig || '情感配置待识别';
  return `这本书的核心打法是“${rhythmPattern}”节奏，主要靠 ${shuangdianTypes} 驱动读者反馈，主线矛盾围绕 ${coreConflict} 展开，核心金手指是 ${cheat}，情感线采用 ${romance} 配置。`;
}

function buildRuleOverviewMetrics(rules: any, bookInfo: BookInfo | null): ReportMetric[] {
  const avgChapterLength = rules?.rhythm?.avgChapterLength
    || (bookInfo?.metadata.totalWords && bookInfo?.metadata.totalChapters
      ? Math.round(bookInfo.metadata.totalWords / bookInfo.metadata.totalChapters)
      : undefined);

  return [
    {
      label: '平均章字数',
      value: typeof avgChapterLength === 'number' ? formatCompactNumber(avgChapterLength) : '未识别',
      ratio: typeof avgChapterLength === 'number'
        ? Math.max(10, Math.min(100, Math.round((avgChapterLength / 6000) * 100)))
        : undefined,
    },
    {
      label: '爽点密度',
      value: formatPercentValue(rules?.shuangdian?.density),
      ratio: normalizeRatio(rules?.shuangdian?.density),
    },
    {
      label: '章末钩子频率',
      value: formatPercentValue(rules?.rhythm?.cliffhangerFrequency),
      ratio: normalizeRatio(rules?.rhythm?.cliffhangerFrequency),
    },
    {
      label: '建议目标篇幅',
      value: typeof rules?.wordCount?.targetWordCount === 'number'
        ? formatCompactNumber(rules.wordCount.targetWordCount)
        : '未识别',
      ratio: typeof rules?.wordCount?.targetWordCount === 'number'
        ? Math.max(10, Math.min(100, Math.round((rules.wordCount.targetWordCount / 3000000) * 100)))
        : undefined,
    },
  ];
}

function buildObservationGroups(report: RuleObservationReport | undefined): ObservationGroup[] {
  if (!report) {
    return [];
  }

  return [
    { key: 'drive', title: '核心驱动', values: pickStrings([report.baseDrive, report.payoffSequenceHint], 2) },
    { key: 'arc', title: '推进单元', values: pickStrings(report.recurringArcUnits, 4) },
    { key: 'goal', title: '章节目标', values: pickStrings(report.recurringGoalPatterns, 5) },
    { key: 'obstacle', title: '主要阻力', values: pickStrings(report.recurringObstaclePatterns, 5) },
    { key: 'outcome', title: '结果链', values: pickStrings(report.recurringOutcomePatterns, 5) },
    { key: 'action', title: '动作母题', values: pickStrings(report.actionMotifs, 5) },
    { key: 'relation', title: '关系模式', values: pickStrings(report.relationPatterns, 5) },
    { key: 'suspense', title: '悬念模式', values: pickStrings(report.suspensePatterns, 5) },
    { key: 'time', title: '时间锚点', values: pickStrings(report.timeAnchorPatterns, 5) },
    { key: 'location', title: '地点母题', values: pickStrings(report.locationPatterns, 5) },
    { key: 'item', title: '物品 / 资源', values: pickStrings(report.itemMotifs, 5) },
    { key: 'traits', title: '主角特征', values: pickStrings(report.protagonistTraits, 4) },
    { key: 'power', title: '圈层 / 权力', values: pickStrings([...report.powerSystemSignals, ...report.factionSignals], 5) },
    { key: 'resource', title: '资源信号', values: pickStrings(report.resourceSignals, 5) },
    { key: 'payoff', title: '爽点信号', values: pickStrings(report.payoffSignals, 5) },
    { key: 'romance', title: '情感信号', values: pickStrings(report.romanceSignals, 5) },
    { key: 'style', title: '文风信号', values: pickStrings(report.styleSignals, 5) },
    { key: 'stage', title: '阶段推进链', values: pickStrings(report.stageProgression, 5) },
    { key: 'scene', title: '高频场景', values: pickStrings(report.recurringSceneTypes, 5) },
  ].filter((group) => group.values.length > 0);
}

function joinSignalText(values: string[], limit = 3, fallback = '未识别') {
  const selected = pickStrings(values, limit);
  return selected.length > 0 ? selected.join('、') : fallback;
}

function buildPromptSuggestions(params: {
  report: RuleObservationReport | undefined;
  rules: RuleSet | null;
  bookInfo: BookInfo | null;
}): PromptSuggestion[] {
  const { report, rules, bookInfo } = params;
  if (!report || !rules) {
    return [];
  }

  const stageChain = joinSignalText(report.stageProgression, 4, '阶段链未识别');
  const goalChain = joinSignalText(report.recurringGoalPatterns, 3, '目标链未识别');
  const obstacleChain = joinSignalText(report.recurringObstaclePatterns, 3, '阻力链未识别');
  const outcomeChain = joinSignalText(report.recurringOutcomePatterns, 3, '结果链未识别');
  const actionChain = joinSignalText(report.actionMotifs, 3, '动作链未识别');
  const relationChain = joinSignalText(report.relationPatterns, 3, '关系变化未识别');
  const suspenseChain = joinSignalText(report.suspensePatterns, 3, '悬念模式未识别');
  const timeChain = joinSignalText(report.timeAnchorPatterns, 3, '时间锚点未识别');
  const locationChain = joinSignalText(report.locationPatterns, 3, '地点母题未识别');
  const itemChain = joinSignalText(report.itemMotifs, 3, '物品资源未识别');
  const sceneChain = joinSignalText(report.recurringSceneTypes, 3, '场景未识别');
  const resourceChain = joinSignalText(report.resourceSignals, 3, '资源未识别');
  const payoffChain = joinSignalText(report.payoffSignals, 3, '反馈点未识别');
  const romanceChain = joinSignalText(report.romanceSignals, 3, '感情信号未识别');
  const wordTarget = bookInfo ? formatCompactNumber(bookInfo.metadata.totalWords) : '目标篇幅未识别';

  return [
    {
      key: 'ruleObservationExtra',
      title: '加强拆书观察',
      reason: '先把章节事实链抽稳，再谈题材总结。',
      content: `观察阶段优先逐章抽取“时间/地点/人物/物品 + 目标->阻力->动作->结果->悬念”，重点关注 ${timeChain}、${locationChain}、${itemChain}、${goalChain}、${obstacleChain}、${actionChain}、${outcomeChain} 和 ${relationChain}，不要只做题材标签归纳。`,
    },
    {
      key: 'ruleSynthesisExtra',
      title: '加强规则提炼',
      reason: '把观察层压成可复用连载引擎。',
      content: `规则提炼必须明确阶段推进链 ${stageChain}，并把“${timeChain} / ${locationChain} / ${itemChain} / ${goalChain} -> ${obstacleChain} -> ${actionChain} -> ${outcomeChain} -> ${suspenseChain}”压成可复用公式，同时交代资源 ${resourceChain} 与场景 ${sceneChain}。`,
    },
    {
      key: 'standardOutlineExtra',
      title: '加强标准大纲',
      reason: '避免大纲只有概括，没有事件推进。',
      content: `标准大纲必须围绕 ${goalChain} 这些阶段目标推进，每章都要交代当前时间 ${timeChain}、地点 ${locationChain}、关键资源 ${itemChain}、阻力 ${obstacleChain}、动作 ${actionChain}、结果反馈 ${outcomeChain}，并保留 ${suspenseChain} 作为后续牵引。`,
    },
    {
      key: 'goldenChapterExtra',
      title: '加强示范正文',
      reason: '让正文一上来就有抓手。',
      content: `示范正文前三段直接切入 ${timeChain} / ${locationChain} / ${sceneChain} 场景，尽早露出关键物品或资源 ${itemChain}，立刻抛出主角当前目标“${goalChain}”和阻力“${obstacleChain}”，优先展示动作 ${actionChain} 与反馈 ${payoffChain}，结尾挂上 ${suspenseChain}。`,
    },
    {
      key: 'threeTierTotalExtra',
      title: '加强总纲',
      reason: '总纲必须写清阶段链和资源战场。',
      content: `总纲要明确 ${stageChain} 的阶段递进，每个阶段写清核心目标、外部阻力、时间节奏 ${timeChain}、主战场 ${locationChain}、关键资源 ${resourceChain} / ${itemChain}、高频场景 ${sceneChain} 与最终反馈 ${payoffChain}，总目标篇幅按 ${wordTarget} 规模控制。`,
    },
    {
      key: 'threeTierVolumeExtra',
      title: '加强卷纲',
      reason: '每卷都要有事件闭环，而不是重复概括。',
      content: `每卷都围绕一个阶段目标展开，至少写清卷内主时间、主战场 ${locationChain}、关键资源 ${itemChain}、卷内最大阻力、关键动作、关系变化 ${relationChain}、阶段性结果 ${outcomeChain} 和卷尾悬念 ${suspenseChain}。`,
    },
    {
      key: 'threeTierChapterExtra',
      title: '加强章纲',
      reason: '章纲要能直接拿去写正文。',
      content: `每章都按“时间 / 地点 / 物品 -> 目标 -> 阻力 -> 动作 -> 结果 -> 悬念”输出，优先使用 ${timeChain}、${locationChain}、${itemChain}、${goalChain}、${obstacleChain}、${actionChain}、${outcomeChain} 这些已抽出的事实链；若有感情线，再补充 ${romanceChain}。`,
    },
  ];
}

function RuleEvidencePanel({ report }: { report: RuleObservationReport | undefined }) {
  const groups = useMemo(() => buildObservationGroups(report), [report]);

  if (!report) {
    return null;
  }

  return (
    <div className="rounded-[28px] border border-amber-200 bg-[linear-gradient(160deg,#fffbeb_0%,#ffffff_52%,#fff7ed_100%)] p-6 shadow-[0_24px_90px_-48px_rgba(180,83,9,0.22)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
            <Sparkles className="h-3.5 w-3.5" />
            规则证据层
          </div>
          <h3 className="mt-3 text-2xl font-semibold text-slate-950">这套规则不是拍脑袋猜的</h3>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            先从分布式章节里抽题材、时间、地点、物品、资源、爽点、情感、场景和阶段推进，再把证据压成规则。这里展示的是规则背后的观察层。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:min-w-[320px]">
          <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">题材判断</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{report.genreGuess || '未识别'}</div>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">核心驱动</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{report.baseDrive || '未识别'}</div>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">桥段母题</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{report.standoutMoments.length > 0 ? `${report.standoutMoments.length}个` : '未识别'}</div>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">章节证据</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{report.chapterObservations.length}章</div>
          </div>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          {groups.map((group) => (
            <div key={group.key} className="rounded-[24px] border border-white/80 bg-white/90 p-5 shadow-sm">
              <div className="text-sm font-semibold text-amber-700">{group.title}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {group.values.map((value) => (
                  <span key={`${group.key}-${value}`} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                    {value}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {report.chapterObservations.length > 0 && (
        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          {report.chapterObservations.map((chapter) => (
            <div key={`${chapter.chapterIndex}-${chapter.title}`} className="rounded-[24px] border border-white/80 bg-white/95 p-5 shadow-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.chapterIndex} 章</span>
                <h4 className="text-base font-semibold text-slate-900">{chapter.title || '未命名章节'}</h4>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">阶段作用</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.stageRole || '未识别'}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">章末钩子</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.hookType || '未识别'}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">时间锚点</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.timeAnchor || '未识别'}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">核心地点</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.location || '未识别'}</div>
                </div>
              </div>
              <div className="mt-4 space-y-3 text-sm leading-7 text-slate-700">
                <p><span className="font-semibold text-slate-900">显性目标：</span>{chapter.explicitGoal || '未识别'}</p>
                <p><span className="font-semibold text-slate-900">主要阻力：</span>{chapter.obstacle || '未识别'}</p>
                <p><span className="font-semibold text-slate-900">章节结果：</span>{chapter.outcome || '未识别'}</p>
                <p><span className="font-semibold text-slate-900">本章回报：</span>{chapter.payoff || '未识别'}</p>
                <p><span className="font-semibold text-slate-900">悬念：</span>{chapter.suspense || '未识别'}</p>
              </div>
              <div className="mt-4 grid gap-3">
                {(chapter.items || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(chapter.items || []).map((item) => (
                      <span key={`${chapter.chapterIndex}-item-${item}`} className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-xs font-medium text-fuchsia-700">
                        物品：{item}
                      </span>
                    ))}
                  </div>
                )}
                {(chapter.sceneFlow || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(chapter.sceneFlow || []).map((item) => (
                      <span key={`${chapter.chapterIndex}-scene-flow-${item}`} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700">
                        场景流：{item}
                      </span>
                    ))}
                  </div>
                )}
                {(chapter.involvedRoles || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(chapter.involvedRoles || []).map((item) => (
                      <span key={`${chapter.chapterIndex}-role-${item}`} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                        人物：{item}
                      </span>
                    ))}
                  </div>
                )}
                {chapter.keyActions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {chapter.keyActions.map((item) => (
                      <span key={`${chapter.chapterIndex}-action-${item}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                        动作：{item}
                      </span>
                    ))}
                  </div>
                )}
                {chapter.relationChanges.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {chapter.relationChanges.map((item) => (
                      <span key={`${chapter.chapterIndex}-relation-${item}`} className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
                        关系：{item}
                      </span>
                    ))}
                  </div>
                )}
                {chapter.sceneTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {chapter.sceneTypes.map((item) => (
                      <span key={`${chapter.chapterIndex}-scene-${item}`} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700">
                        场景：{item}
                      </span>
                    ))}
                  </div>
                )}
                {chapter.powerSignals.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {chapter.powerSignals.map((item) => (
                      <span key={`${chapter.chapterIndex}-power-${item}`} className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700">
                        资源：{item}
                      </span>
                    ))}
                  </div>
                )}
                {chapter.romanceSignals.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {chapter.romanceSignals.map((item) => (
                      <span key={`${chapter.chapterIndex}-romance-${item}`} className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                        情感：{item}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RULE_SECTION_CONFIG: Record<RuleSectionKey, RuleSectionConfig> = {
  rhythm: {
    title: '节奏引擎',
    description: '看这本书如何安排快慢、高潮和钩子。',
    icon: BarChart3,
    theme: '节奏',
    panelClass: 'border-emerald-200 bg-[linear-gradient(145deg,rgba(240,253,244,0.9),rgba(236,253,245,0.55))]',
    chipClass: 'border-emerald-200 bg-white/90 text-emerald-800',
    metricClass: 'bg-emerald-500',
    extract: (section) => ({
      headline: section?.rhythmPattern || '章节推进节奏待识别',
      metrics: [
        { label: '平均章字数', value: typeof section?.avgChapterLength === 'number' ? formatCompactNumber(section.avgChapterLength) : '未识别' },
        { label: '钩子频率', value: formatPercentValue(section?.cliffhangerFrequency), ratio: normalizeRatio(section?.cliffhangerFrequency) },
      ],
      bullets: pickStrings([section?.tensionBuilding, section?.rhythmPattern]),
    }),
  },
  shuangdian: {
    title: '爽点配方',
    description: '看用户为什么愿意继续点下一章。',
    icon: TrendingUp,
    theme: '爽点',
    panelClass: 'border-amber-200 bg-[linear-gradient(145deg,rgba(255,251,235,0.95),rgba(255,247,237,0.72))]',
    chipClass: 'border-amber-200 bg-white/90 text-amber-800',
    metricClass: 'bg-amber-500',
    extract: (section) => ({
      headline: section?.favoriteSequence || '爽点连招待识别',
      metrics: [
        { label: '密度', value: formatPercentValue(section?.density), ratio: normalizeRatio(section?.density) },
        { label: '核心类型', value: pickStrings(section?.types, 2).join(' / ') || '未识别' },
      ],
      bullets: [...pickStrings(section?.types, 3), ...pickStrings(section?.patterns, 3)].slice(0, 5),
    }),
  },
  character: {
    title: '人物模板',
    description: '看主配角如何被设计成可持续推动剧情的机器。',
    icon: Users,
    theme: '人物',
    panelClass: 'border-rose-200 bg-[linear-gradient(145deg,rgba(255,241,242,0.95),rgba(255,247,237,0.58))]',
    chipClass: 'border-rose-200 bg-white/90 text-rose-800',
    metricClass: 'bg-rose-500',
    extract: (section) => ({
      headline: section?.protagonistTemplate || '主角模板待识别',
      metrics: [
        { label: '主角', value: section?.protagonistTemplate || '未识别' },
        { label: '反派', value: section?.antagonistTemplate || '未识别' },
      ],
      bullets: [...pickStrings(section?.supportingTemplates, 3), ...pickStrings(section?.relationshipPatterns, 3)].slice(0, 5),
    }),
  },
  worldview: {
    title: '世界规则',
    description: '看势力、修炼体系和经济单位怎么支撑长篇连载。',
    icon: Globe2,
    theme: '世界',
    panelClass: 'border-cyan-200 bg-[linear-gradient(145deg,rgba(236,254,255,0.95),rgba(239,246,255,0.7))]',
    chipClass: 'border-cyan-200 bg-white/90 text-cyan-800',
    metricClass: 'bg-cyan-500',
    extract: (section) => ({
      headline: section?.powerSystem || '力量体系待识别',
      metrics: [
        { label: '修炼体系', value: section?.powerSystem || '未识别' },
        { label: '经济单位', value: section?.currency || '未识别' },
      ],
      bullets: [...pickStrings(section?.factions, 3), ...pickStrings(section?.worldRules, 3)].slice(0, 5),
    }),
  },
  cheats: {
    title: '金手指',
    description: '看主角依靠什么突破普通人的成长曲线。',
    icon: Target,
    theme: '外挂',
    panelClass: 'border-fuchsia-200 bg-[linear-gradient(145deg,rgba(253,244,255,0.95),rgba(250,245,255,0.75))]',
    chipClass: 'border-fuchsia-200 bg-white/90 text-fuchsia-800',
    metricClass: 'bg-fuchsia-500',
    extract: (section) => ({
      headline: section?.mainCheatType || '金手指待识别',
      metrics: [
        { label: '主金手指', value: section?.mainCheatType || '未识别' },
        { label: '升级方式', value: section?.escalationPattern || '未识别' },
      ],
      bullets: pickStrings([section?.表现形式, section?.escalationPattern]),
    }),
  },
  romance: {
    title: '情感线',
    description: '看关系推进是调味剂，还是主力留存点。',
    icon: Heart,
    theme: '感情',
    panelClass: 'border-pink-200 bg-[linear-gradient(145deg,rgba(253,242,248,0.96),rgba(255,247,237,0.68))]',
    chipClass: 'border-pink-200 bg-white/90 text-pink-800',
    metricClass: 'bg-pink-500',
    extract: (section) => ({
      headline: section?.loveInterestConfig || '情感配置待识别',
      metrics: [
        { label: '配置', value: section?.loveInterestConfig || '未识别' },
        { label: '推进方式', value: section?.romancePace || '未识别' },
      ],
      bullets: pickStrings(section?.keyRomanceEvents, 4),
    }),
  },
  dungeon: {
    title: '地图副本',
    description: '看剧情靠什么场景切换和资源刷新来抬高兴奋值。',
    icon: MapPinned,
    theme: '副本',
    panelClass: 'border-indigo-200 bg-[linear-gradient(145deg,rgba(238,242,255,0.96),rgba(239,246,255,0.68))]',
    chipClass: 'border-indigo-200 bg-white/90 text-indigo-800',
    metricClass: 'bg-indigo-500',
    extract: (section) => ({
      headline: section?.mapConfig || '地图配置待识别',
      metrics: [
        { label: '地图逻辑', value: section?.mapConfig || '未识别' },
        { label: '怪物设定', value: section?.monsterSettings || '未识别' },
      ],
      bullets: pickStrings(section?.dungeonTypes, 4),
    }),
  },
  conflict: {
    title: '冲突主轴',
    description: '看整本书的矛盾是如何层层放大的。',
    icon: Swords,
    theme: '冲突',
    panelClass: 'border-orange-200 bg-[linear-gradient(145deg,rgba(255,247,237,0.96),rgba(255,251,235,0.72))]',
    chipClass: 'border-orange-200 bg-white/90 text-orange-800',
    metricClass: 'bg-orange-500',
    extract: (section) => ({
      headline: section?.coreConflict || '核心冲突待识别',
      metrics: [
        { label: '主冲突', value: section?.coreConflict || '未识别' },
        { label: '结局指向', value: section?.endingType || '未识别' },
      ],
      bullets: pickStrings(section?.phaseConflicts, 4),
    }),
  },
  style: {
    title: '文风与叙事',
    description: '看文本为什么读起来顺不顺、冲不冲、像不像爆款。',
    icon: PenTool,
    theme: '文风',
    panelClass: 'border-slate-300 bg-[linear-gradient(145deg,rgba(248,250,252,0.98),rgba(241,245,249,0.82))]',
    chipClass: 'border-slate-300 bg-white/90 text-slate-700',
    metricClass: 'bg-slate-500',
    extract: (section) => ({
      headline: section?.languageStyle || '语言风格待识别',
      metrics: [
        { label: '叙事节奏', value: section?.narrativePace || '未识别' },
        { label: '描写浓度', value: section?.descriptionDensity || '未识别' },
      ],
      bullets: pickStrings([section?.languageStyle, section?.narrativePace, section?.descriptionDensity]),
    }),
  },
  wordCount: {
    title: '篇幅规划',
    description: '看这本书天然适合写成什么量级、用什么埋点频率。',
    icon: FileText,
    theme: '篇幅',
    panelClass: 'border-lime-200 bg-[linear-gradient(145deg,rgba(247,254,231,0.96),rgba(240,253,244,0.76))]',
    chipClass: 'border-lime-200 bg-white/90 text-lime-800',
    metricClass: 'bg-lime-500',
    extract: (section) => ({
      headline: section?.foreshadowingPattern || '埋伏节奏待识别',
      metrics: [
        { label: '目标章节', value: typeof section?.targetChapterCount === 'number' ? `${section.targetChapterCount}章` : '未识别' },
        { label: '目标字数', value: typeof section?.targetWordCount === 'number' ? formatCompactNumber(section.targetWordCount) : '未识别' },
      ],
      bullets: pickStrings([section?.foreshadowingPattern]),
    }),
  },
};

function MiniMeter({ ratio, className }: { ratio?: number; className: string }) {
  if (typeof ratio !== 'number') {
    return null;
  }

  return (
    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-black/5">
      <div className={`h-full rounded-full ${className}`} style={{ width: `${ratio}%` }} />
    </div>
  );
}

function ReportStatCard({ label, value, hint, ratio, toneClass }: ReportMetric & { hint?: string; toneClass: string }) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm shadow-slate-200/60 backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
          {hint ? <p className="mt-2 text-xs leading-5 text-slate-500">{hint}</p> : null}
        </div>
        <div className={`h-10 w-10 rounded-2xl ${toneClass} opacity-80`} />
      </div>
      <MiniMeter ratio={ratio} className={toneClass} />
    </div>
  );
}

function summarizeRole(role?: string): string {
  const roleMap: Record<string, string> = {
    protagonist: '主角',
    antagonist: '反派',
    supporting: '配角',
    mentor: '导师',
    'love-interest': '感情线',
  };

  return role ? roleMap[role] || role : '角色待判定';
}

function summarizeRarity(rarity?: string): string {
  const rarityMap: Record<string, string> = {
    common: '普通',
    uncommon: '良好',
    rare: '稀有',
    epic: '史诗',
    legendary: '传说',
  };
  return rarity ? rarityMap[rarity] || rarity : '未评级';
}

function summarizeImportance(importance?: string): string {
  const map: Record<string, string> = {
    low: '低',
    normal: '常规',
    medium: '中',
    high: '高',
    critical: '核心',
  };
  return importance ? map[importance] || importance : '常规';
}



export default function V3Page() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('upload');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  
  // 数据状态
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(null);
  const [rulesInfo, setRulesInfo] = useState<RulesInfo | null>(null);
  const [generatedResult, setGeneratedResult] = useState<GeneratedResult | null>(null);
  const [deepResult, setDeepResult] = useState<any>(null);
  const [deepProgress, setDeepProgress] = useState(0);
  const [threeTierResult, setThreeTierResult] = useState<ThreeTierResult | null>(null);
  const [resultRedirectCountdown, setResultRedirectCountdown] = useState<number | null>(null);
  const [resultRedirectPaused, setResultRedirectPaused] = useState(false);
  const [resultRedirectTarget, setResultRedirectTarget] = useState<string | null>(null);
  const activeAnalyzeTaskRef = useRef<string | null>(null);
  const analyzeTaskRecoveryRef = useRef<Record<string, number>>({});
  const activeGenerationTaskRef = useRef<string | null>(null);
  
  // 生成参数
  const [genre, setGenre] = useState('都市');
  const [desiredWords, setDesiredWords] = useState(500000);
  const [threeTierPlanMode, setThreeTierPlanMode] = useState<ChapterPlanMode>('target-words');
  const [style, setStyle] = useState('');
  const [customRequirements, setCustomRequirements] = useState('');
  const [isThreeTierMode, setIsThreeTierMode] = useState(true);
  const [promptProfiles, setPromptProfiles] = useState<PromptProfile[]>(() => loadPromptProfiles());
  const [activePromptProfileId, setActivePromptProfileId] = useState<string>(() => loadActivePromptProfileId(loadPromptProfiles()));

  // 文件上传相关
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [encoding, setEncoding] = useState<'utf-8' | 'gbk' | 'auto'>('auto');

  const resetResultRedirect = useCallback(() => {
    setResultRedirectCountdown(null);
    setResultRedirectPaused(false);
    setResultRedirectTarget(null);
  }, []);

  const armResultRedirect = useCallback((outlineId?: string | null) => {
    if (!outlineId) {
      resetResultRedirect();
      return;
    }

    setResultRedirectTarget(`/history/${outlineId}`);
    setResultRedirectPaused(false);
    setResultRedirectCountdown(6);
  }, [resetResultRedirect]);

  const handleOpenResultHistory = useCallback(() => {
    if (!resultRedirectTarget) {
      return;
    }

    navigate(resultRedirectTarget);
  }, [navigate, resultRedirectTarget]);

  const handleToggleResultRedirect = useCallback(() => {
    if (resultRedirectPaused) {
      setResultRedirectPaused(false);
      setResultRedirectCountdown((current) => {
        if (current === null || current < 3) {
          return 3;
        }
        return current;
      });
      return;
    }

    setResultRedirectPaused(true);
  }, [resultRedirectPaused]);

  const activePromptProfile = useMemo(() => {
    return promptProfiles.find((profile) => profile.id === activePromptProfileId) || promptProfiles[0] || DEFAULT_PROMPT_PROFILES[0];
  }, [activePromptProfileId, promptProfiles]);
  const activePromptConfig = useMemo<PromptConfig | undefined>(() => {
    if (!activePromptProfile) {
      return undefined;
    }

    return {
      ...activePromptProfile.promptConfig,
      profileId: activePromptProfile.id,
      profileName: activePromptProfile.name,
    };
  }, [activePromptProfile]);

  useEffect(() => {
    savePromptProfiles(promptProfiles);
  }, [promptProfiles]);

  useEffect(() => {
    if (!activePromptProfileId || !promptProfiles.some((profile) => profile.id === activePromptProfileId)) {
      const fallbackId = promptProfiles[0]?.id || DEFAULT_PROMPT_PROFILES[0].id;
      setActivePromptProfileId(fallbackId);
      saveActivePromptProfileId(fallbackId);
      return;
    }

    saveActivePromptProfileId(activePromptProfileId);
  }, [activePromptProfileId, promptProfiles]);

  useEffect(() => {
    if (!resultRedirectTarget || resultRedirectPaused || resultRedirectCountdown === null) {
      return;
    }

    if (resultRedirectCountdown <= 0) {
      navigate(resultRedirectTarget);
      return;
    }

    const timer = window.setTimeout(() => {
      setResultRedirectCountdown((current) => {
        if (current === null) {
          return null;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [navigate, resultRedirectCountdown, resultRedirectPaused, resultRedirectTarget]);

  // 处理拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // 处理文件拖放
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  // 处理文件选择
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const applyAnalyzeResult = useCallback((result: RulesInfo) => {
    activeAnalyzeTaskRef.current = null;
    analyzeTaskRecoveryRef.current = {};
    setRulesInfo(result);
    setProgress('规则提取完成！');

    setTimeout(() => {
      setStage('rules');
    }, 500);
  }, []);

  const pollAnalyzeTask = useCallback((taskId: string) => {
    activeAnalyzeTaskRef.current = taskId;

    const poll = async () => {
      if (activeAnalyzeTaskRef.current !== taskId) {
        return;
      }

      try {
        const task = await v3GetAnalyzeTask(taskId);
        if (activeAnalyzeTaskRef.current !== taskId) {
          return;
        }

        setProgress(task.progressMessage || '正在分析小说结构，提取写作规则...');

        if (task.status === 'processing' && task.isStale) {
          const lastRecoveredAt = analyzeTaskRecoveryRef.current[taskId] || 0;
          const now = Date.now();

          if (now - lastRecoveredAt >= ANALYZE_TASK_RECOVERY_COOLDOWN_MS) {
            analyzeTaskRecoveryRef.current[taskId] = now;
            setProgress('检测到后台任务中断，正在自动恢复拆书分析...');
            v3RunAnalyzeTask(taskId);
          }
        }

        if (task.status === 'completed') {
          if (task.resultData) {
            applyAnalyzeResult(task.resultData);
            return;
          }

          activeAnalyzeTaskRef.current = null;
          setError('拆书任务已完成，但结果数据缺失');
          setStage('uploaded');
          return;
        }

        if (task.status === 'failed') {
          activeAnalyzeTaskRef.current = null;
          delete analyzeTaskRecoveryRef.current[taskId];
          setError(`上传成功，但自动分析失败：${task.errorMessage || '分析失败'}`);
          setStage('uploaded');
          return;
        }
      } catch (err) {
        console.error('[V3] Analyze poll error:', err);
      }

      if (activeAnalyzeTaskRef.current === taskId) {
        setTimeout(poll, 2000);
      }
    };

    setTimeout(poll, 1000);
  }, [applyAnalyzeResult]);

  // 阶段 1: 上传书籍
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('请先选择要上传的小说文件');
      return;
    }

    resetResultRedirect();
    setError(null);
    setRulesInfo(null);
    setGeneratedResult(null);
    setDeepResult(null);
    setThreeTierResult(null);
    activeAnalyzeTaskRef.current = null;
    analyzeTaskRecoveryRef.current = {};
    activeGenerationTaskRef.current = null;
    setStage('uploading');
    setProgress('正在上传小说文件...');

    try {
      // 根据文件大小选择上传方式
      const fileSize = selectedFile.size;
      const useChunked = fileSize >= CHUNKED_UPLOAD_THRESHOLD;

      let result: BookInfo;

      if (useChunked) {
        result = await v3UploadBookChunked(selectedFile, encoding, (progress) => {
          setProgress(`正在上传小说文件... ${progress}%`);
        });
      } else {
        try {
          result = await v3UploadBook(selectedFile, encoding);
        } catch (uploadError) {
          if (!shouldFallbackToChunkedUpload(uploadError)) {
            throw uploadError;
          }

          setProgress('直传不稳定，正在切换为分片上传...');
          result = await v3UploadBookChunked(selectedFile, encoding, (progress) => {
            setProgress(`正在分片上传小说文件... ${progress}%`);
          });
        }
      }

      setBookInfo(result);
      setThreeTierPlanMode(
        result.metadata.totalChapters > 0 && result.metadata.totalChapters <= MAX_SOURCE_CHAPTER_PLAN_COUNT
          ? 'source-book'
          : 'target-words'
      );
      setStage('uploaded');
      setProgress('文件上传成功，正在启动拆书分析...');
      void handleAnalyze(result.bookId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
      setStage('upload');
    }
  };

  // 阶段 2: 分析书籍提取规则
  const handleAnalyze = useCallback(async (bookId?: string) => {
    const targetBookId = bookId || bookInfo?.bookId;
    if (!targetBookId) {
      setError('缺少书籍 ID');
      setStage('upload');
      return;
    }

    setStage('analyzing');
    setProgress('正在启动拆书分析任务...');
    setError(null);

    try {
      console.log('[V3] Starting analyze task for bookId:', targetBookId);
      const task = await v3StartAnalyzeTask({
        bookId: targetBookId,
        options: { fastMode: true },
        promptConfig: activePromptConfig,
      });
      console.log('[V3] Analyze task started:', task);

      analyzeTaskRecoveryRef.current[task.taskId] = Date.now();
      v3RunAnalyzeTask(task.taskId);
      setProgress(task.progressMessage || '拆书任务已创建，正在发起执行请求...');

      if (task.status === 'completed') {
        const taskDetail = await v3GetAnalyzeTask(task.taskId);
        if (taskDetail.status === 'completed' && taskDetail.resultData) {
          applyAnalyzeResult(taskDetail.resultData);
          return;
        }
      }

      pollAnalyzeTask(task.taskId);
    } catch (err) {
      console.error('[V3] Analyze error:', err);
      const errorMessage = err instanceof Error ? err.message : '分析失败';
      setError(`上传成功，但自动分析失败：${errorMessage}`);
      setStage('uploaded');
    }
  }, [activePromptConfig, applyAnalyzeResult, bookInfo?.bookId, pollAnalyzeTask]);

  const handleAddPromptProfile = useCallback(() => {
    const nextProfile: PromptProfile = {
      id: `profile_${Date.now()}`,
      name: `新方案 ${promptProfiles.length + 1}`,
      description: '在这里写这个提示词方案想强化的方向。',
      promptConfig: { ...activePromptProfile?.promptConfig },
    };

    setPromptProfiles((current) => [...current, nextProfile]);
    setActivePromptProfileId(nextProfile.id);
  }, [activePromptProfile?.promptConfig, promptProfiles.length]);

  const handleDuplicatePromptProfile = useCallback(() => {
    const baseProfile = activePromptProfile || promptProfiles[0] || DEFAULT_PROMPT_PROFILES[0];
    if (!baseProfile) {
      return;
    }

    const nextProfile: PromptProfile = {
      id: `profile_${Date.now()}`,
      name: `${baseProfile.name} 副本`,
      description: baseProfile.description || '从当前方案复制而来。',
      promptConfig: { ...baseProfile.promptConfig },
    };

    setPromptProfiles((current) => [...current, nextProfile]);
    setActivePromptProfileId(nextProfile.id);
  }, [activePromptProfile, promptProfiles]);

  const handleUpdatePromptProfile = useCallback((profileId: string, updater: (profile: PromptProfile) => PromptProfile) => {
    setPromptProfiles((current) => current.map((profile) => (
      profile.id === profileId ? updater(profile) : profile
    )));
  }, []);

  const handleResetPromptProfile = useCallback(() => {
    if (!activePromptProfile) {
      return;
    }

    const defaultProfile = getDefaultPromptProfileById(activePromptProfile.id) || DEFAULT_PROMPT_PROFILES[0];
    handleUpdatePromptProfile(activePromptProfile.id, (profile) => ({
      ...profile,
      name: getDefaultPromptProfileById(activePromptProfile.id)?.name || profile.name,
      description: getDefaultPromptProfileById(activePromptProfile.id)?.description || profile.description,
      promptConfig: { ...defaultProfile.promptConfig },
    }));
  }, [activePromptProfile, handleUpdatePromptProfile]);

  const handleApplyPromptSuggestion = useCallback((suggestion: PromptSuggestion, mode: 'append' | 'replace') => {
    if (!activePromptProfile) {
      return;
    }

    handleUpdatePromptProfile(activePromptProfile.id, (profile) => {
      const previousValue = (profile.promptConfig[suggestion.key] || '').trim();
      const nextValue = mode === 'replace' || !previousValue
        ? suggestion.content
        : `${previousValue}\n${suggestion.content}`;

      return {
        ...profile,
        promptConfig: {
          ...profile.promptConfig,
          [suggestion.key]: nextValue,
        },
      };
    });
  }, [activePromptProfile, handleUpdatePromptProfile]);

  const handleDeletePromptProfile = useCallback((profileId: string) => {
    if (promptProfiles.length <= 1) {
      return;
    }

    const nextProfiles = promptProfiles.filter((profile) => profile.id !== profileId);
    setPromptProfiles(nextProfiles);
    if (activePromptProfileId === profileId) {
      setActivePromptProfileId(nextProfiles[0]?.id || DEFAULT_PROMPT_PROFILES[0].id);
    }
  }, [activePromptProfileId, promptProfiles]);

  const pollGenerationTask = useCallback((taskId: string, mode: 'standard' | 'three-tier') => {
    activeGenerationTaskRef.current = taskId;

    const poll = async () => {
      if (activeGenerationTaskRef.current !== taskId) {
        return;
      }

      try {
        const task = await v3GetGenerationTask(taskId);
        if (activeGenerationTaskRef.current !== taskId) {
          return;
        }

        setProgress(task.progressMessage || (mode === 'three-tier' ? '正在生成三层级大纲...' : '正在生成普通大纲...'));

        if (task.status === 'completed') {
          activeGenerationTaskRef.current = null;

          if (mode === 'standard' && task.resultData) {
            setGeneratedResult(task.resultData);
            armResultRedirect(task.resultData.outlineId);
            setStage('result');
            return;
          }

          if (mode === 'three-tier' && task.resultData) {
            setThreeTierResult(task.resultData);
            armResultRedirect(task.resultData.outlineId);
            setStage('three-tier-result');
            return;
          }

          resetResultRedirect();
          setError('任务已完成，但结果数据缺失');
          setStage('rules');
          return;
        }

        if (task.status === 'failed') {
          activeGenerationTaskRef.current = null;
          resetResultRedirect();
          setError(task.errorMessage || '生成失败');
          setStage('rules');
          return;
        }
      } catch (err) {
        console.error('[V3] Generation poll error:', err);
      }

      if (activeGenerationTaskRef.current === taskId) {
        setTimeout(poll, 2000);
      }
    };

    setTimeout(poll, 1000);
  }, [armResultRedirect, resetResultRedirect]);

  const startGenerationTask = async (mode: 'standard' | 'three-tier') => {
    if (!rulesInfo) {
      setError('缺少规则集 ID');
      return;
    }

    resetResultRedirect();
    setStage(mode === 'three-tier' ? 'three-tier-generating' : 'generating');
    setProgress(mode === 'three-tier' ? '正在创建三层级生成任务...' : '正在创建普通生成任务...');
    setError(null);

    try {
      const task = await v3StartGenerationTask({
        mode,
        ruleSetId: rulesInfo.ruleSetId,
        userInput: {
          genre,
          desiredWords,
          chapterPlanMode: mode === 'three-tier' ? threeTierPlanMode : undefined,
          style: style || undefined,
          customRequirements: customRequirements || undefined,
        },
        promptConfig: activePromptConfig,
      });

      v3RunGenerationTask(task.taskId);
      setProgress(task.progressMessage || (mode === 'three-tier' ? '三层级生成任务已创建，正在发起执行请求...' : '普通生成任务已创建，正在发起执行请求...'));
      pollGenerationTask(task.taskId, mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : mode === 'three-tier' ? '三层级大纲生成失败' : '生成失败');
      setStage('rules');
    }
  };

  // 阶段 3: 生成大纲
  const handleGenerate = async () => {
    await startGenerationTask('standard');
  };

  // 阶段 3b: 生成三层级大纲
  const handleGenerateThreeTier = async () => {
    await startGenerationTask('three-tier');
  };

  // 阶段 4: 深度分析
  const handleDeepAnalyze = async () => {
    if (!bookInfo?.bookId) {
      setError('缺少书籍 ID');
      return;
    }

    setStage('deep-analyzing');
    setProgress('正在启动深度分析...');
    setError(null);
    setDeepProgress(0);

    let finished = false;
    let kickoffError: string | null = null;
    let hasSeenProgress = false;

    const failDeepAnalyze = (message: string) => {
      if (finished) return;
      finished = true;
      setError(`深度分析失败: ${message}`);
      setStage('rules');
    };

    // 先异步触发深度分析任务，再立刻轮询进度。
    // 这样即使长请求还没返回，页面也能展示真实进度。
    void v3DeepAnalyze(bookInfo.bookId)
      .then((result) => {
        console.log('[V3] Deep analyze started:', result);
      })
      .catch((err) => {
        kickoffError = err instanceof Error ? err.message : '深度分析失败';
        console.error('[V3] Deep analyze kickoff error:', err);
      });

    const pollProgress = async () => {
      if (finished) return;

      try {
        const progressData = await v3GetProgress(bookInfo.bookId);
        hasSeenProgress = true;
        setDeepProgress(progressData.progress);

        if (progressData.status === 'completed') {
          const summaryData = await v3GetSummary(bookInfo.bookId);
          finished = true;
          setDeepResult(summaryData.summary);
          setStage('deep-result');
          return;
        }

        if (progressData.status === 'failed') {
          failDeepAnalyze(progressData.overallSummary || '深度分析失败');
          return;
        }
      } catch (pollErr) {
        console.error('[V3] Progress poll error:', pollErr);

        if (kickoffError && !hasSeenProgress) {
          failDeepAnalyze(kickoffError);
          return;
        }
      }

      if (finished) return;
      setTimeout(pollProgress, hasSeenProgress ? 3000 : 2000);
    };

    setTimeout(pollProgress, 1500);
  };

  // 重新开始
  const handleRestart = () => {
    activeAnalyzeTaskRef.current = null;
    activeGenerationTaskRef.current = null;
    resetResultRedirect();
    setStage('upload');
    setBookInfo(null);
    setRulesInfo(null);
    setGeneratedResult(null);
    setSelectedFile(null);
    setError(null);
    setProgress('');
    // 清除深度分析数据
    setDeepResult(null);
    setDeepProgress(0);
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    if (num >= 10000) {
      return `${(num / 10000).toFixed(1)}万`;
    }
    return num.toLocaleString();
  };

  // 流派选项
  const genreOptions = [
    '都市', '玄幻', '仙侠', '历史', '军事', '科幻', '游戏', '竞技',
    '悬疑', '轻小说', '二次元', '现实主义', '短篇小说'
  ];

  // 字数选项
  const wordCountOptions = [
    { label: '30万字（短篇）', value: 300000 },
    { label: '50万字（中篇）', value: 500000 },
    { label: '80万字（中长篇）', value: 800000 },
    { label: '100万字（长篇）', value: 1000000 },
    { label: '150万字（超长篇）', value: 1500000 },
  ];

  const currentRules = rulesInfo?.rules ?? null;
  const currentObservationReport = (currentRules?.observationReport || undefined) as RuleObservationReport | undefined;
  const deepSummary = deepResult as DeepSummary | null;
  const sourceChapterCount = bookInfo?.metadata.totalChapters || currentRules?.wordCount?.targetChapterCount || 0;
  const sourceChapterPlanAvailable = sourceChapterCount > 0 && sourceChapterCount <= MAX_SOURCE_CHAPTER_PLAN_COUNT;
  const ruleOverviewMetrics = currentRules ? buildRuleOverviewMetrics(currentRules, bookInfo) : [];
  const ruleDigest = currentRules ? buildRuleDigest(currentRules) : '';
  const promptSuggestions = useMemo(
    () => buildPromptSuggestions({ report: currentObservationReport, rules: currentRules, bookInfo }),
    [bookInfo, currentObservationReport, currentRules]
  );
  const analysisPromptFields = useMemo(
    () => PROMPT_FIELD_CONFIG.filter((field) => field.stage === 'analysis'),
    []
  );
  const generationPromptFields = useMemo(
    () => PROMPT_FIELD_CONFIG.filter((field) => field.stage === 'generation'),
    []
  );
  const activeRuleSections = currentRules
    ? RULE_SECTION_ORDER.filter((key) => Boolean(currentRules[key]))
    : [];
  const resultRulePanels = currentRules
    ? activeRuleSections.slice(0, 6).map((key) => {
      const config = RULE_SECTION_CONFIG[key];
      return {
        key,
        title: config.title,
        icon: config.icon,
        panelClass: config.panelClass,
        chipClass: config.chipClass,
        presentation: config.extract(currentRules[key]),
      };
    })
    : [];
  const threeTierVolumeGroups = threeTierResult
    ? threeTierResult.outline.volumes.map((volume) => {
      const startChapter = volume.chapters[0]?.index ?? 0;
      const endChapter = volume.chapters[volume.chapters.length - 1]?.index ?? 0;

      return {
        ...volume,
        chapterRangeLabel: startChapter && endChapter ? `第 ${startChapter} - ${endChapter} 章` : '未分配章节',
        chapterOutlines: threeTierResult.outline.chapterOutlines.filter((chapter) => (
          chapter.index >= startChapter && chapter.index <= endChapter
        )),
      };
    })
    : [];
  const deepSummaryCards: Array<ReportMetric & { toneClass: string; hint: string }> = deepSummary ? [
    {
      label: '人物角色',
      value: `${deepSummary.characters?.length || 0}`,
      hint: '拆出的主要角色数量',
      ratio: Math.min(100, (deepSummary.characters?.length || 0) * 10),
      toneClass: 'bg-rose-500',
    },
    {
      label: '世界地点',
      value: `${deepSummary.locations?.length || 0}`,
      hint: '已记录的场景与副本',
      ratio: Math.min(100, (deepSummary.locations?.length || 0) * 12),
      toneClass: 'bg-cyan-500',
    },
    {
      label: '伏笔总量',
      value: `${deepSummary.foreshadowing?.total || 0}`,
      hint: `已回收 ${deepSummary.foreshadowing?.harvested || 0} 条`,
      ratio: deepSummary.foreshadowing?.total
        ? Math.round(((deepSummary.foreshadowing.harvested || 0) / deepSummary.foreshadowing.total) * 100)
        : 0,
      toneClass: 'bg-amber-500',
    },
    {
      label: '关键物品',
      value: `${deepSummary.items?.length || 0}`,
      hint: '法宝、资源、技能等关键载体',
      ratio: Math.min(100, (deepSummary.items?.length || 0) * 12),
      toneClass: 'bg-emerald-500',
    },
  ] : [];
  const resultRedirectNotice = resultRedirectTarget ? (
    <div className="overflow-hidden rounded-[28px] border border-emerald-200 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.16),transparent_35%),linear-gradient(135deg,rgba(236,253,245,0.95),rgba(255,255,255,0.98))] p-5 shadow-[0_24px_80px_-55px_rgba(16,185,129,0.55)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
            <CheckCircle className="h-3.5 w-3.5" />
            已保存到历史记录
          </div>
          <h3 className="mt-3 text-xl font-semibold text-slate-950">
            {resultRedirectPaused
              ? '结果已经落到历史详情页，你可以继续停留，也可以随时打开完整工作台。'
              : `结果已保存，${resultRedirectCountdown ?? 0} 秒后自动跳转到历史详情。`}
          </h3>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            历史详情里可以继续查看卷纲、章纲、示范正文和拆书维度，不会只停留在当前摘要页。
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleOpenResultHistory}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-600"
          >
            <BookOpen className="h-4 w-4" />
            立即查看历史详情
          </button>
          <button
            type="button"
            onClick={handleToggleResultRedirect}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            {resultRedirectPaused ? <RefreshCw className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
            {resultRedirectPaused ? '恢复自动跳转' : '留在当前页'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 标题 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-500" />
            智能拆书 V3
          </h1>
          <p className="text-slate-500">
            上传完整小说，AI 提取写作规则，基于规则生成大纲
          </p>
        </div>

        {/* 进度指示器 */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['upload', 'rules', 'result'].map((s, idx) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                getProgressStepIndex(stage) === idx
                  ? 'bg-emerald-500 text-white'
                  : getProgressStepIndex(stage) > idx
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'bg-slate-100 text-slate-400'
              }`}>
                {getProgressStepIndex(stage) > idx ? (
                  <CheckCircle className="w-5 h-5" />
                ) : idx + 1}
              </div>
              {idx < 2 && (
                <div className={`w-12 h-0.5 mx-1 ${
                  getProgressStepIndex(stage) > idx
                    ? 'bg-emerald-500'
                    : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-4">
          <span>上传小说</span>
          <span>配置生成</span>
          <span>查看结果</span>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium mb-1">操作失败</p>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                关闭
              </button>
            </div>
          </div>
        )}

        {/* 阶段 1: 上传 */}
        <AnimatePresence mode="wait">
          {stage === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_360px] xl:items-start"
            >
              <div className="space-y-6">
                <div className="rounded-[30px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.12),transparent_36%),linear-gradient(160deg,#f8fafc_0%,#ffffff_54%,#ecfeff_100%)] p-6 shadow-[0_24px_90px_-52px_rgba(15,23,42,0.22)]">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-2xl">
                      <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                        <Sparkles className="h-3.5 w-3.5" />
                        长篇 TXT 拆书工作台
                      </div>
                      <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                        先上传原书，再把拆书规则压成可复用生产线
                      </h2>
                      <p className="mt-3 text-sm leading-7 text-slate-600">
                        桌面端恢复宽屏布局。左侧负责上传和启动分析，右侧直接说明这套链路会产出什么，避免页面看起来像竖屏 App。
                      </p>
                    </div>

                    <div className="rounded-3xl border border-white/80 bg-white/85 p-4 text-sm shadow-sm shadow-slate-200/50 backdrop-blur md:min-w-[320px]">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">当前引擎</div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">MiniMax M2.5</div>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">输入</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">长篇 TXT</div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">输出</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">规则 / 卷纲 / 章纲</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-100 bg-white/90 p-4">
                    <span className="text-sm text-slate-600">文件编码：</span>
                    <div className="flex flex-wrap gap-2">
                      {(['auto', 'utf-8', 'gbk'] as const).map((enc) => (
                        <button
                          key={enc}
                          onClick={() => setEncoding(enc)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                            encoding === enc
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                          }`}
                        >
                          {enc === 'auto' ? '自动检测' : enc.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div
                    className={`relative mt-6 border-2 border-dashed rounded-[28px] p-8 transition-all duration-200 ${
                      dragActive
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-300 hover:border-emerald-400'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileInput}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />

                    <div className="text-center">
                      {selectedFile ? (
                        <div className="space-y-2">
                          <FileText className="w-12 h-12 mx-auto text-emerald-500" />
                          <p className="text-slate-600 font-medium">{selectedFile.name}</p>
                          <p className="text-sm text-slate-400">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(null);
                            }}
                            className="text-sm text-red-500 hover:text-red-600"
                          >
                            重新选择
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center bg-slate-100">
                            <UploadIcon className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-600 mb-2">
                            拖拽 .txt 文件到此处，或点击上传
                          </p>
                          <p className="text-sm text-slate-400">
                            支持完整小说文本，建议 30 万字以上
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || stage !== 'upload'}
                    className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {stage !== 'upload' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        处理中...
                      </>
                    ) : (
                      <>
                        <UploadIcon className="w-5 h-5" />
                        上传并分析
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-6 xl:sticky xl:top-24">
                <div className="rounded-[28px] border border-blue-100 bg-[linear-gradient(145deg,rgba(239,246,255,0.95),rgba(255,255,255,0.98))] p-5 shadow-[0_24px_80px_-55px_rgba(59,130,246,0.25)]">
                  <p className="text-sm text-blue-700 font-medium mb-2">V3 版本说明</p>
                  <ul className="text-sm text-blue-600 space-y-2 leading-6">
                    <li>• 上传完整小说，AI 自动提取写作规则</li>
                    <li>• 基于提取的规则生成个性化大纲</li>
                    <li>• 生成大纲 + 黄金三章示范</li>
                    <li>• 使用 MiniMax M2.5 模型</li>
                  </ul>
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-55px_rgba(15,23,42,0.18)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">本次输出</div>
                  <div className="mt-4 space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-semibold text-slate-900">拆书规则</div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">节奏、爽点、人物、世界、冲突、文风、篇幅规划。</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-semibold text-slate-900">事实证据层</div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">时间、地点、物品、目标、阻力、结果、动作链、关系变化。</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-semibold text-slate-900">生成结果</div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">总纲、卷纲、章纲，以及可继续补修的示范正文。</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {stage === 'uploaded' && bookInfo && (
            <motion.div
              key="uploaded"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-500" />
                  文件已上传
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400 mb-1">书名</p>
                    <p className="font-medium text-slate-700">{bookInfo.title}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">章节数</p>
                    <p className="font-medium text-slate-700">{bookInfo.metadata.totalChapters}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">总字数</p>
                    <p className="font-medium text-slate-700">{formatNumber(bookInfo.metadata.totalWords)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">编码</p>
                    <p className="font-medium text-slate-700">{bookInfo.metadata.encoding}</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-sm text-amber-800 font-medium mb-1">上传成功，但自动分析没有完成</p>
                <p className="text-sm text-amber-700">你不需要重新上传文件，可以直接重试分析。</p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => void handleAnalyze(bookInfo.bookId)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25"
                >
                  <Sparkles className="w-5 h-5" />
                  继续分析
                </button>
                <button
                  onClick={handleRestart}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  重新上传
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 分析进度 */}
        {(stage === 'uploading' || stage === 'analyzing' || stage === 'generating' || stage === 'three-tier-generating') && (
          <div className={`rounded-xl p-8 border text-center ${
            stage === 'three-tier-generating' 
              ? 'bg-blue-50 border-blue-100' 
              : stage === 'uploading'
                ? 'bg-slate-50 border-slate-200'
              : 'bg-emerald-50 border-emerald-100'
          }`}>
            <Loader2 className={`w-12 h-12 mx-auto mb-4 animate-spin ${
              stage === 'three-tier-generating'
                ? 'text-blue-500'
                : stage === 'uploading'
                  ? 'text-slate-500'
                  : 'text-emerald-500'
            }`} />
            <p className={`text-lg font-medium mb-2 ${
              stage === 'three-tier-generating'
                ? 'text-blue-700'
                : stage === 'uploading'
                  ? 'text-slate-700'
                  : 'text-emerald-700'
            }`}>{progress}</p>
            <p className={`text-sm ${
              stage === 'three-tier-generating'
                ? 'text-blue-600'
                : stage === 'uploading'
                  ? 'text-slate-600'
                  : 'text-emerald-600'
            }`}>
              {stage === 'three-tier-generating' 
                ? '三层级大纲生成中，请耐心等待（总纲→卷纲→章纲）...'
                : stage === 'uploading'
                  ? '正在安全上传长篇文本，请不要关闭页面...'
                  : '请耐心等待，这可能需要几分钟...'
              }
            </p>
          </div>
        )}

        {/* 深度分析进度 */}
        {stage === 'deep-analyzing' && (
          <div className="bg-purple-50 rounded-xl p-8 border border-purple-100 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-500 animate-spin" />
            <p className="text-lg font-medium text-purple-700 mb-4">{progress}</p>
            <div className="w-full max-w-md mx-auto">
              <div className="flex justify-between text-sm text-purple-600 mb-2">
                <span>分析进度</span>
                <span>{Math.round(deepProgress)}%</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-purple-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${deepProgress}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-4">深度分析需要几分钟，请耐心等待...</p>
          </div>
        )}

        {/* 阶段 2: 规则配置 */}
        <AnimatePresence mode="wait">
          {stage === 'rules' && bookInfo && rulesInfo && (
            <motion.div
              key="rules"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.16),transparent_38%),radial-gradient(circle_at_85%_18%,rgba(59,130,246,0.16),transparent_24%),linear-gradient(160deg,#f8fafc_0%,#ffffff_55%,#ecfeff_100%)] p-6 shadow-[0_24px_90px_-48px_rgba(15,23,42,0.42)]">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
                      <Sparkles className="h-3.5 w-3.5" />
                      MiniMax M2.5 拆书报告
                    </div>
                    <h3 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                      {bookInfo.title}
                    </h3>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                      {ruleDigest}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 rounded-3xl border border-white/70 bg-white/75 p-4 text-sm shadow-sm shadow-slate-200/50 backdrop-blur md:min-w-[360px]">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">章节</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{bookInfo.metadata.totalChapters}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">总字数</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{formatNumber(bookInfo.metadata.totalWords)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">编码</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{bookInfo.metadata.encoding}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {ruleOverviewMetrics.map((metric) => (
                    <ReportStatCard
                      key={metric.label}
                      {...metric}
                      hint="来自当前文本的拆书归纳"
                      toneClass="bg-emerald-500"
                    />
                  ))}
                </div>

                {(rulesInfo.contextMode || activePromptProfile) && (
                  <div className="mt-5 grid gap-3 lg:grid-cols-3">
                    <div className="rounded-2xl border border-white/80 bg-white/85 p-4 text-sm text-slate-600">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">规则提取路径</div>
                      <div className="mt-2 font-semibold text-slate-900">
                        {rulesInfo.contextMode === 'chapter_yaml' ? '结构化章节 facts 优先' : '原文样本优先'}
                      </div>
                      <div className="mt-2 text-xs leading-5 text-slate-500">
                        {rulesInfo.contextMode === 'chapter_yaml'
                          ? `已调用 ${rulesInfo.contextStats?.storedStructuredChapters || 0} 章结构化数据，当前采样 ${rulesInfo.contextStats?.sampledStructuredChapters || 0} 章。`
                          : `当前主要依赖 ${rulesInfo.contextStats?.sampledTextChapters || 0} 章原文样本。`}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/85 p-4 text-sm text-slate-600">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">分析方案</div>
                      <div className="mt-2 font-semibold text-slate-900">{activePromptProfile?.name || '未选择'}</div>
                      <div className="mt-2 text-xs leading-5 text-slate-500">
                        当前拆书分析和后续生成都会使用这套提示词工作台配置。
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/85 p-4 text-sm text-slate-600">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">分析焦点</div>
                      <div className="mt-2 font-semibold text-slate-900">
                        {currentObservationReport
                          ? `${currentObservationReport.chapterObservations.length} 章证据 / ${pickStrings(currentObservationReport.recurringGoalPatterns, 2).join('、') || '待补强'}`
                          : '待提取'}
                      </div>
                      <div className="mt-2 text-xs leading-5 text-slate-500">
                        现在规则优先围绕目标、阻力、结果、动作链和悬念做压缩，而不是只做题材标签。
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                {activeRuleSections.map((key) => {
                  const config = RULE_SECTION_CONFIG[key];
                  const section = currentRules?.[key];
                  const presentation = config.extract(section);
                  const Icon = config.icon;

                  return (
                    <div
                      key={key}
                      className={`rounded-[26px] border p-5 shadow-[0_20px_65px_-45px_rgba(15,23,42,0.48)] ${config.panelClass}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            {config.theme}
                          </div>
                          <h3 className="mt-4 text-xl font-semibold text-slate-900">{config.title}</h3>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{config.description}</p>
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/80 bg-white/80 shadow-sm shadow-slate-200/60">
                          <Icon className="h-5 w-5 text-slate-800" />
                        </div>
                      </div>

                      <div className="mt-5 rounded-2xl border border-white/80 bg-white/85 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">核心判断</p>
                        <p className="mt-2 text-base font-medium leading-7 text-slate-900">{presentation.headline}</p>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        {presentation.metrics.map((metric) => (
                          <div key={metric.label} className="rounded-2xl border border-white/70 bg-white/75 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{metric.label}</span>
                              <span className="text-sm font-semibold text-slate-900">{metric.value}</span>
                            </div>
                            <MiniMeter ratio={metric.ratio} className={config.metricClass} />
                          </div>
                        ))}
                      </div>

                      {presentation.bullets.length > 0 ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {presentation.bullets.map((bullet) => (
                            <span
                              key={bullet}
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${config.chipClass}`}
                            >
                              {bullet}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <RuleEvidencePanel report={currentObservationReport} />

              {/* 生成配置 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-emerald-500" />
                  生成配置
                </h3>
                
                <div className="space-y-4">
                  {/* 流派选择 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      小说流派
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {genreOptions.map((g) => (
                        <button
                          key={g}
                          onClick={() => setGenre(g)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                            genre === g
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isThreeTierMode && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        三层级章数规划方式
                      </label>
                      <div className="grid gap-3 md:grid-cols-2">
                        <button
                          type="button"
                          disabled={!sourceChapterPlanAvailable}
                          onClick={() => sourceChapterPlanAvailable && setThreeTierPlanMode('source-book')}
                          className={`rounded-xl border p-4 text-left transition-all ${
                            threeTierPlanMode === 'source-book'
                              ? 'border-blue-500 bg-blue-50 shadow-sm'
                              : sourceChapterPlanAvailable
                                ? 'border-slate-200 bg-white hover:border-blue-300'
                                : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                          }`}
                        >
                          <div className="text-sm font-semibold text-slate-900">按原书章节拆解</div>
                          <p className="mt-2 text-xs leading-6 text-slate-500">
                            {sourceChapterPlanAvailable
                              ? `直接跟随原书 ${sourceChapterCount} 章来做卷纲和章纲，不再把拆书结果压成 68 章这种新规划。`
                              : sourceChapterCount > MAX_SOURCE_CHAPTER_PLAN_COUNT
                                ? `原书共 ${sourceChapterCount} 章，当前版本最多支持 ${MAX_SOURCE_CHAPTER_PLAN_COUNT} 章逐章三层拆解。`
                                : '上传并分析完成后，系统会自动识别原书章节数。'}
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => setThreeTierPlanMode('target-words')}
                          className={`rounded-xl border p-4 text-left transition-all ${
                            threeTierPlanMode === 'target-words'
                              ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <div className="text-sm font-semibold text-slate-900">按目标字数重规划</div>
                          <p className="mt-2 text-xs leading-6 text-slate-500">
                            这是“新书规划”模式，会按目标篇幅重新生成卷数和章数，结果不等于原书真实章节数。
                          </p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 字数选择 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isThreeTierMode && threeTierPlanMode === 'source-book' ? '重规划目标字数（当前模式不生效）' : '目标字数'}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {wordCountOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={isThreeTierMode && threeTierPlanMode === 'source-book'}
                          onClick={() => setDesiredWords(opt.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            desiredWords === opt.value
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          } disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {isThreeTierMode && threeTierPlanMode === 'source-book' && sourceChapterCount > 0 ? (
                      <p className="mt-2 text-xs leading-6 text-slate-500">
                        当前会优先跟随原书 <span className="font-semibold text-slate-900">{sourceChapterCount} 章</span> 生成三层级结构，目标字数只作为参考展示，不参与章数规划。
                      </p>
                    ) : null}
                  </div>

                  {/* 风格要求 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      风格要求（可选）
                    </label>
                    <input
                      type="text"
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      placeholder="例如：轻松幽默、虐心、热血..."
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>

                  {/* 自定义需求 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      自定义需求（可选）
                    </label>
                    <textarea
                      value={customRequirements}
                      onChange={(e) => setCustomRequirements(e.target.value)}
                      placeholder="描述你想要的特殊设定、剧情走向等..."
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-h-[100px] resize-y"
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-2xl">
                        <div className="text-sm font-medium text-slate-900">提示词工作台</div>
                        <p className="mt-1 text-sm leading-6 text-slate-500">
                          这里不是焊死提示词。现在同一套方案会同时影响“拆书分析”和“生成结果”，你可以为不同项目建立多套方案，随时切换、复制、重置或注入基于当前拆书证据的建议。
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void handleAnalyze(bookInfo.bookId)}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
                        >
                          <Sparkles className="h-4 w-4" />
                          按当前方案重跑拆书
                        </button>
                        <button
                          type="button"
                          onClick={handleAddPromptProfile}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          <Sparkles className="h-4 w-4 text-emerald-500" />
                          新增方案
                        </button>
                        <button
                          type="button"
                          onClick={handleDuplicatePromptProfile}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          <RefreshCw className="h-4 w-4 text-sky-500" />
                          复制当前方案
                        </button>
                        <button
                          type="button"
                          onClick={handleResetPromptProfile}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-white px-4 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-50"
                        >
                          <Wand2 className="h-4 w-4" />
                          {getDefaultPromptProfileById(activePromptProfile?.id || '') ? '恢复内置模板' : '套用默认策划版'}
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {promptProfiles.map((profile) => (
                        <button
                          key={profile.id}
                          type="button"
                          onClick={() => setActivePromptProfileId(profile.id)}
                          className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                            activePromptProfile?.id === profile.id
                              ? 'bg-slate-900 text-white'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          {profile.name}
                        </button>
                      ))}
                    </div>

                    {activePromptProfile && (
                      <div className="mt-4 space-y-4 rounded-2xl border border-white/90 bg-white p-4 shadow-sm">
                        <div className="grid gap-4 md:grid-cols-[1fr,1.2fr]">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">方案名称</label>
                            <input
                              type="text"
                              value={activePromptProfile.name}
                              onChange={(e) => handleUpdatePromptProfile(activePromptProfile.id, (profile) => ({
                                ...profile,
                                name: e.target.value,
                              }))}
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">方案说明</label>
                            <input
                              type="text"
                              value={activePromptProfile.description}
                              onChange={(e) => handleUpdatePromptProfile(activePromptProfile.id, (profile) => ({
                                ...profile,
                                description: e.target.value,
                              }))}
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm text-slate-600">
                          <span>当前生效方案：<span className="font-semibold text-slate-900">{activePromptProfile.name}</span></span>
                          <button
                            type="button"
                            onClick={() => handleDeletePromptProfile(activePromptProfile.id)}
                            disabled={promptProfiles.length <= 1}
                            className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            删除当前方案
                          </button>
                        </div>

                        {promptSuggestions.length > 0 && (
                          <div className="space-y-3 rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                            <div>
                              <div className="text-sm font-semibold text-sky-900">基于当前拆书结果的一键建议</div>
                              <p className="mt-1 text-sm leading-6 text-slate-600">
                                这些建议不是固定模板，而是根据当前这本书的目标链、阻力链、动作链、关系变化和阶段推进自动生成。你可以直接追加到某个字段，或者覆盖当前字段。
                              </p>
                            </div>
                            <div className="grid gap-3 xl:grid-cols-2">
                              {promptSuggestions.map((suggestion) => {
                                const fieldMeta = PROMPT_FIELD_CONFIG.find((field) => field.key === suggestion.key);
                                return (
                                  <article key={`${suggestion.key}-${suggestion.title}`} className="rounded-2xl border border-white/80 bg-white/95 p-4 shadow-sm">
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <div className="text-sm font-semibold text-sky-700">{suggestion.title}</div>
                                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{fieldMeta?.label || suggestion.key}</div>
                                      </div>
                                      <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700">
                                        {fieldMeta?.stage === 'analysis' ? '分析阶段' : '生成阶段'}
                                      </span>
                                    </div>
                                    <p className="mt-3 text-sm leading-6 text-slate-600">{suggestion.reason}</p>
                                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-7 text-slate-700">
                                      {suggestion.content}
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleApplyPromptSuggestion(suggestion, 'append')}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                                      >
                                        追加到字段
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleApplyPromptSuggestion(suggestion, 'replace')}
                                        className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-medium text-sky-700 transition hover:bg-sky-100"
                                      >
                                        覆盖当前字段
                                      </button>
                                    </div>
                                  </article>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="grid gap-4">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                            <div className="text-sm font-semibold text-slate-900">A. 拆书分析阶段</div>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                              这部分会直接影响规则提取本身，也就是你能不能把小说拆成有用的目标链、阻力链、动作链和关系模式。
                            </p>
                            <div className="mt-4 grid gap-4">
                              {analysisPromptFields.map((field) => (
                                <div key={field.key}>
                                  <label className="mb-2 block text-sm font-medium text-slate-700">{field.label}</label>
                                  <textarea
                                    value={activePromptProfile.promptConfig[field.key] || ''}
                                    onChange={(e) => handleUpdatePromptProfile(activePromptProfile.id, (profile) => ({
                                      ...profile,
                                      promptConfig: {
                                        ...profile.promptConfig,
                                        [field.key]: e.target.value,
                                      },
                                    }))}
                                    placeholder={field.placeholder}
                                    className="min-h-[88px] w-full resize-y rounded-lg border border-slate-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                            <div className="text-sm font-semibold text-slate-900">B. 生成阶段</div>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                              这部分会影响标准大纲、三层级总纲/卷纲/章纲和示范正文的具体落地方式。
                            </p>
                            <div className="mt-4 grid gap-4">
                              {generationPromptFields.map((field) => (
                                <div key={field.key}>
                                  <label className="mb-2 block text-sm font-medium text-slate-700">{field.label}</label>
                                  <textarea
                                    value={activePromptProfile.promptConfig[field.key] || ''}
                                    onChange={(e) => handleUpdatePromptProfile(activePromptProfile.id, (profile) => ({
                                      ...profile,
                                      promptConfig: {
                                        ...profile.promptConfig,
                                        [field.key]: e.target.value,
                                      },
                                    }))}
                                    placeholder={field.placeholder}
                                    className="min-h-[88px] w-full resize-y rounded-lg border border-slate-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 生成模式选择 */}
              <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-emerald-500" />
                  选择生成模式
                </h3>
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-blue-100 bg-white/80 px-4 py-3 text-sm text-slate-600">
                  <span className="inline-flex items-center rounded-full bg-blue-500 px-2.5 py-1 text-xs font-semibold text-white">
                    默认推荐
                  </span>
                  当前默认优先使用三层级大纲，方便后续直接查看总纲、卷纲、章纲的完整可视化结果。
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 普通模式 */}
                  <div 
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      !isThreeTierMode 
                        ? 'border-emerald-500 bg-white shadow-md' 
                        : 'border-slate-200 bg-white/50 hover:border-emerald-300'
                    }`}
                    onClick={() => setIsThreeTierMode(false)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-emerald-500" />
                      <span className="font-medium text-slate-900">标准拆书</span>
                    </div>
                    <p className="text-sm text-slate-500">生成完整大纲 + 黄金三章</p>
                    <p className="text-xs text-slate-400 mt-1">适合：快速生成、字数50万以内</p>
                  </div>
                  {/* 三层级模式 */}
                  <div 
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      isThreeTierMode 
                        ? 'border-blue-500 bg-white shadow-md' 
                        : 'border-slate-200 bg-white/50 hover:border-blue-300'
                    }`}
                    onClick={() => setIsThreeTierMode(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <BookOpen className="w-5 h-5 text-blue-500" />
                      <span className="font-medium text-slate-900">三层级大纲</span>
                      <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                        默认推荐
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">总纲 → 卷纲 → 章纲</p>
                    <p className="text-xs text-slate-400 mt-1">适合：长篇连载、字数50万以上、需要卷纲可视化</p>
                  </div>
                </div>
              </div>

              {/* 生成按钮 */}

              {/* 生成按钮 */}
              <button
                onClick={isThreeTierMode ? handleGenerateThreeTier : handleGenerate}
                disabled={stage !== 'rules'}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  isThreeTierMode 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-blue-500/25'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/25'
                }`}
              >
                {(stage as string) === 'generating' || (stage as string) === 'three-tier-generating' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    {isThreeTierMode ? '生成三层级拆书结果' : '生成标准拆书结果'}
                  </>
                )}
              </button>

              {/* 重新分析 */}
              <button
                onClick={() => handleAnalyze()}
                className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all cursor-pointer bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4" />
                重新分析
              </button>

              {/* 深度分析按钮 - 仅在有bookInfo时显示 */}
              {bookInfo?.bookId && (
                <button
                  onClick={handleDeepAnalyze}
                  disabled={stage !== 'rules'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="w-5 h-5" />
                  深度分析
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* 阶段 3: 结果展示 */}
        <AnimatePresence mode="wait">
          {stage === 'result' && generatedResult && (
            <motion.div
              key="result"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {resultRedirectNotice}

              {/* 大纲信息 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {generatedResult.outline.title}
                    </h3>
                    <p className="text-sm text-emerald-600 mt-1">
                      {generatedResult.outline.genre} · {generatedResult.outline.chapters.length}章
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-sm rounded-full">
                    生成成功
                  </span>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-500 mb-2">故事简介</h4>
                  <p className="text-slate-700 break-words">{generatedResult.outline.synopsis}</p>
                </div>

                {/* 章节大纲 */}
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-3">章节大纲</h4>
                  <div className="space-y-3">
                    {generatedResult.outline.chapters.map((chapter) => (
                      <div key={chapter.index} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-emerald-600">
                            第{chapter.index}章
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatNumber(chapter.wordCount)}字
                          </span>
                        </div>
                        <h5 className="font-medium text-slate-900 mb-1">{chapter.title}</h5>
                        <p className="text-sm text-slate-600 mb-2 break-words whitespace-pre-wrap">{chapter.summary}</p>
                        {chapter.keyPoints && chapter.keyPoints.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {chapter.keyPoints.map((point, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-white border border-slate-200 rounded text-slate-500">
                                {point}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 黄金三章 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  黄金三章示范
                </h3>
                
                <div className="space-y-4">
                  {['chapter1', 'chapter2', 'chapter3'].map((chapter, idx) => (
                    <div key={chapter} className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-slate-900 mb-2">
                        第{idx + 1}章示范
                      </h4>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">
                        {generatedResult.goldenChapters[chapter as keyof typeof generatedResult.goldenChapters]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {currentRules && (
                <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(160deg,#f8fafc_0%,#ffffff_58%,#ecfeff_100%)] p-6 shadow-[0_24px_90px_-48px_rgba(15,23,42,0.36)]">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-3xl">
                      <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-700">
                        <Sparkles className="h-3.5 w-3.5" />
                        多维拆书快照
                      </div>
                      <h3 className="mt-3 text-2xl font-semibold text-slate-950">这份规则到底拆出了什么</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{ruleDigest}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 lg:min-w-[320px]">
                      {ruleOverviewMetrics.slice(0, 4).map((metric) => (
                        <div key={metric.label} className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                          <div className="text-xs uppercase tracking-[0.18em] text-slate-400">{metric.label}</div>
                          <div className="mt-2 text-lg font-semibold text-slate-900">{metric.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {resultRulePanels.map((panel) => {
                      const Icon = panel.icon;
                      return (
                        <div key={panel.key} className={`rounded-[24px] border p-5 shadow-sm ${panel.panelClass}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{panel.title}</div>
                              <div className="mt-2 text-lg font-semibold text-slate-950">{panel.presentation.headline}</div>
                            </div>
                            <div className="rounded-full bg-white/90 p-2 text-slate-700 shadow-sm">
                              <Icon className="h-4 w-4" />
                            </div>
                          </div>
                          {panel.presentation.metrics.length > 0 && (
                            <div className="mt-4 grid gap-3">
                              {panel.presentation.metrics.slice(0, 2).map((metric) => (
                                <div key={metric.label} className="rounded-2xl border border-white/80 bg-white/90 p-3">
                                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{metric.label}</div>
                                  <div className="mt-1 text-sm font-semibold text-slate-900">{metric.value}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {panel.presentation.bullets.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {panel.presentation.bullets.slice(0, 4).map((bullet) => (
                                <span key={bullet} className={`rounded-full border px-3 py-1 text-xs ${panel.chipClass}`}>
                                  {bullet}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <RuleEvidencePanel report={currentObservationReport} />

              {/* 操作按钮 */}
              <div className="grid gap-4 md:grid-cols-2">
                <button
                  type="button"
                  onClick={handleOpenResultHistory}
                  disabled={!resultRedirectTarget}
                  className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 font-medium text-white transition-all hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <BookOpen className="w-5 h-5" />
                  查看历史详情
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 font-medium text-slate-600 transition-all hover:bg-slate-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  重新开始
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 阶段 4: 深度分析结果 */}
        <AnimatePresence mode="wait">
          {stage === 'deep-result' && deepResult && (
            <motion.div
              key="deep-result"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="overflow-hidden rounded-[30px] border border-indigo-200 bg-[radial-gradient(circle_at_top_left,rgba(168,85,247,0.18),transparent_34%),radial-gradient(circle_at_84%_18%,rgba(59,130,246,0.18),transparent_24%),linear-gradient(160deg,#eef2ff_0%,#ffffff_54%,#ecfeff_100%)] p-6 shadow-[0_30px_100px_-55px_rgba(79,70,229,0.52)]">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-700">
                      <Route className="h-3.5 w-3.5" />
                      深度拆书驾驶舱
                    </div>
                    <h3 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">剧情、人物、伏笔一屏看透</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                      这份报告把长篇文本里的成长弧光、角色阵列、伏笔回收和场景资源拆成了可读结构。它的价值不只是“看懂原书”，而是让你知道什么地方值得模仿、哪些机制能直接复用。
                    </p>
                  </div>
                  <div className="rounded-3xl border border-white/70 bg-white/75 px-5 py-4 text-right shadow-sm shadow-slate-200/50 backdrop-blur">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">已处理章节</p>
                    <p className="mt-2 text-3xl font-semibold text-slate-950">{deepSummary?.totalChapters || 0}</p>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {deepSummaryCards.map((card) => (
                    <ReportStatCard
                      key={card.label}
                      label={card.label}
                      value={card.value}
                      hint={card.hint}
                      ratio={card.ratio}
                      toneClass={card.toneClass}
                    />
                  ))}
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[1.25fr_0.95fr]">
                <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                    <TrendingUp className="h-5 w-5 text-indigo-500" />
                    主角成长弧光
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">按章节观察主角实力、心态和收获的跃迁节点。</p>
                  <div className="mt-6 space-y-4">
                    {deepSummary?.protagonistArc?.length ? deepSummary.protagonistArc.map((arc, idx) => (
                      <div key={`${arc.chapter}-${idx}`} className="relative rounded-3xl border border-indigo-100 bg-[linear-gradient(155deg,#eef2ff,#ffffff)] p-5">
                        <div className="absolute left-5 top-5 h-[calc(100%-2.5rem)] w-px bg-indigo-100" />
                        <div className="relative ml-5">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-400">Chapter {arc.chapter}</p>
                              <p className="mt-2 text-lg font-semibold text-slate-950">{arc.realm || '阶段跃迁'}</p>
                            </div>
                            <div className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-medium text-indigo-700">
                              第 {arc.chapter} 章
                            </div>
                          </div>
                          <p className="mt-3 text-sm leading-6 text-slate-600">{arc.mental || '暂无心态描述'}</p>
                          {arc.gains?.length ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {arc.gains.slice(0, 5).map((gain) => (
                                <span key={gain} className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-medium text-indigo-700">
                                  {gain}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )) : (
                      <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">当前还没有足够的成长节点数据。</p>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                    <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                      <Route className="h-5 w-5 text-emerald-500" />
                      主线推进轴
                    </h3>
                    <div className="mt-5 space-y-3">
                      {deepSummary?.plotProgress?.length ? deepSummary.plotProgress.slice(0, 8).map((plot) => (
                        <div key={plot.chapter} className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-semibold text-emerald-700">第 {plot.chapter} 章</span>
                            {plot.romance ? <span className="rounded-full bg-white px-2.5 py-1 text-[11px] text-pink-600">情感线推进</span> : null}
                          </div>
                          <p className="mt-2 text-sm leading-6 text-slate-700">{plot.main}</p>
                          {plot.revenge ? <p className="mt-2 text-xs text-orange-600">复仇线：{plot.revenge}</p> : null}
                        </div>
                      )) : (
                        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">暂无主线推进数据。</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                    <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                      <BarChart3 className="h-5 w-5 text-amber-500" />
                      伏笔回收面板
                    </h3>
                    <div className="mt-5 grid grid-cols-3 gap-3">
                      <ReportStatCard label="总伏笔" value={`${deepSummary?.foreshadowing?.total || 0}`} toneClass="bg-amber-500" />
                      <ReportStatCard label="已回收" value={`${deepSummary?.foreshadowing?.harvested || 0}`} toneClass="bg-emerald-500" />
                      <ReportStatCard label="待回收" value={`${deepSummary?.foreshadowing?.planted || 0}`} toneClass="bg-sky-500" />
                    </div>
                    <div className="mt-5 space-y-3">
                      {deepSummary?.foreshadowing?.details?.length ? deepSummary.foreshadowing.details.slice(0, 8).map((detail, idx) => {
                        const plantedChapter = detail.chapterPlanted || detail.chapter || 0;
                        const harvestedChapter = detail.chapterHarvested;
                        return (
                          <div key={`${detail.content || detail.description || 'detail'}-${idx}`} className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-700">
                                第 {plantedChapter} 章埋设
                              </span>
                              {harvestedChapter ? (
                                <span className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                                  第 {harvestedChapter} 章回收
                                </span>
                              ) : (
                                <span className="rounded-full border border-sky-200 bg-white px-2.5 py-1 text-[11px] font-medium text-sky-700">
                                  尚未回收
                                </span>
                              )}
                              {detail.importance ? (
                                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
                                  重要度 {summarizeImportance(detail.importance)}
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-3 text-sm leading-6 text-slate-700">{detail.content || detail.description || '暂无描述'}</p>
                          </div>
                        );
                      }) : (
                        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">暂无伏笔明细。</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
                <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                    <Users className="h-5 w-5 text-rose-500" />
                    角色阵容
                  </h3>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {deepSummary?.characters?.length ? deepSummary.characters.slice(0, 10).map((char, idx) => (
                      <div key={`${char.name}-${idx}`} className="rounded-2xl border border-rose-100 bg-[linear-gradient(145deg,#fff1f2,#ffffff)] p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-base font-semibold text-slate-900">{char.name}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-rose-500">{summarizeRole(char.roleType || char.role)}</p>
                          </div>
                          {typeof char.firstAppearance === 'number' ? (
                            <span className="rounded-full border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-medium text-rose-700">
                              初登场 第 {char.firstAppearance} 章
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )) : (
                      <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">暂无角色数据。</p>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                    <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                      <Package className="h-5 w-5 text-emerald-500" />
                      物品谱系
                    </h3>
                    <div className="mt-5 space-y-3">
                      {deepSummary?.items?.length ? deepSummary.items.slice(0, 8).map((item, idx) => (
                        <div key={`${item.name}-${idx}`} className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                          <div className="flex items-center justify-between gap-4">
                            <p className="font-semibold text-slate-900">{item.name}</p>
                            <span className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                              {summarizeRarity(item.rarity)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                            {item.type ? <span>类型：{item.type}</span> : null}
                            {item.owner ? <span>持有者：{item.owner}</span> : null}
                            {typeof item.chapterAppeared === 'number' ? <span>出现章节：第 {item.chapterAppeared} 章</span> : null}
                          </div>
                        </div>
                      )) : (
                        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">暂无关键物品数据。</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_24px_90px_-55px_rgba(15,23,42,0.45)]">
                    <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                      <MapPinned className="h-5 w-5 text-cyan-500" />
                      世界地图
                    </h3>
                    <div className="mt-5 space-y-3">
                      {deepSummary?.locations?.length ? deepSummary.locations.slice(0, 8).map((location, idx) => (
                        <div key={`${location.name}-${idx}`} className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-slate-900">{location.name}</p>
                            <span className="rounded-full border border-cyan-200 bg-white px-2.5 py-1 text-[11px] font-medium text-cyan-700">
                              {location.isDungeon ? '副本' : '地点'}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span>类型：{location.type}</span>
                            <span>重要度：{summarizeImportance(location.importance)}</span>
                            <span>解锁：第 {location.chapterUnlocked} 章</span>
                            <span>{location.cleared ? '已打通' : '未完全探索'}</span>
                          </div>
                          {location.description ? <p className="mt-2 text-sm leading-6 text-slate-600">{location.description}</p> : null}
                        </div>
                      )) : (
                        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">暂无地图与地点数据。</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-4">
                <button
                  onClick={() => setStage('result')}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  返回大纲
                </button>
                <button
                  onClick={handleRestart}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <CheckCircle className="w-5 h-5" />
                  重新开始
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 阶段 3b: 三层级结果展示 */}
        <AnimatePresence mode="wait">
          {stage === 'three-tier-result' && threeTierResult && (
            <motion.div
              key="three-tier-result"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {resultRedirectNotice}

              {/* 标题信息 */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {threeTierResult.outline.totalOutline.title}
                    </h3>
                    <p className="text-sm text-blue-600 mt-1">
                      {threeTierResult.outline.totalOutline.genre} · {threeTierResult.outline.volumes.length}卷 · 本次生成 {threeTierResult.planningMeta?.plannedChapterCount || threeTierResult.outline.chapterOutlines.length}章
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
                    三层级大纲
                  </span>
                </div>
                <div className="mb-4 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full bg-white/80 px-3 py-1">
                    {getThreeTierPlanModeLabel(threeTierResult.planningMeta?.chapterPlanMode)}
                  </span>
                  {typeof threeTierResult.planningMeta?.sourceChapterCount === 'number' ? (
                    <span className="rounded-full bg-white/80 px-3 py-1">
                      原书真实 {threeTierResult.planningMeta.sourceChapterCount} 章
                    </span>
                  ) : null}
                  <span className="rounded-full bg-white/80 px-3 py-1">
                    本次生成 {threeTierResult.planningMeta?.plannedChapterCount || threeTierResult.outline.chapterOutlines.length} 章
                  </span>
                </div>
                <p className="text-slate-700 break-words">{threeTierResult.outline.totalOutline.synopsis}</p>
                <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm leading-6 text-amber-900">
                  {getThreeTierPlanSummary(
                    threeTierResult.planningMeta,
                    threeTierResult.outline.chapterOutlines.length
                  )}
                </div>
              </div>

              {currentRules && (
                <div className="rounded-[28px] border border-indigo-200 bg-[linear-gradient(160deg,#eef2ff_0%,#ffffff_52%,#ecfeff_100%)] p-6 shadow-[0_24px_90px_-48px_rgba(79,70,229,0.35)]">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-3xl">
                      <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-indigo-700">
                        <Sparkles className="h-3.5 w-3.5" />
                        拆书规则雷达
                      </div>
                      <h3 className="mt-3 text-2xl font-semibold text-slate-950">这份三层级结果真正复用了哪些写法</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{ruleDigest}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 lg:min-w-[320px]">
                      {ruleOverviewMetrics.slice(0, 4).map((metric) => (
                        <div key={metric.label} className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                          <div className="text-xs uppercase tracking-[0.18em] text-slate-400">{metric.label}</div>
                          <div className="mt-2 text-lg font-semibold text-slate-900">{metric.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {resultRulePanels.map((panel) => {
                      const Icon = panel.icon;
                      return (
                        <div key={panel.key} className={`rounded-[24px] border p-5 shadow-sm ${panel.panelClass}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{panel.title}</div>
                              <div className="mt-2 text-lg font-semibold text-slate-950">{panel.presentation.headline}</div>
                            </div>
                            <div className="rounded-full bg-white/90 p-2 text-slate-700 shadow-sm">
                              <Icon className="h-4 w-4" />
                            </div>
                          </div>
                          {panel.presentation.metrics.length > 0 && (
                            <div className="mt-4 grid gap-3">
                              {panel.presentation.metrics.slice(0, 2).map((metric) => (
                                <div key={metric.label} className="rounded-2xl border border-white/80 bg-white/90 p-3">
                                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{metric.label}</div>
                                  <div className="mt-1 text-sm font-semibold text-slate-900">{metric.value}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {panel.presentation.bullets.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {panel.presentation.bullets.slice(0, 4).map((bullet) => (
                                <span key={bullet} className={`rounded-full border px-3 py-1 text-xs ${panel.chipClass}`}>
                                  {bullet}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <RuleEvidencePanel report={currentObservationReport} />

              {/* 总纲详情 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                  全书总纲
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">主角设定</p>
                    <p className="mt-2 text-sm leading-7 text-slate-700 break-words whitespace-pre-wrap">{threeTierResult.outline.totalOutline.protagonist}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">核心冲突</p>
                    <p className="mt-2 text-sm leading-7 text-slate-700 break-words whitespace-pre-wrap">{threeTierResult.outline.totalOutline.coreConflict}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">结局走向</p>
                    <p className="mt-2 text-sm leading-7 text-slate-700 break-words whitespace-pre-wrap">{threeTierResult.outline.totalOutline.ending}</p>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-5">
                  <p className="text-xs uppercase tracking-[0.16em] text-blue-500">总纲摘要</p>
                  <p className="mt-3 text-sm leading-8 text-slate-700 whitespace-pre-wrap break-words">{threeTierResult.outline.totalOutline.totalSummary}</p>
                </div>
              </div>

              {/* 卷纲 + 章纲 */}
              <div className="space-y-5">
                {threeTierVolumeGroups.map((volume) => (
                  <div key={volume.number} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                          第 {volume.number} 卷
                        </div>
                        <h3 className="mt-3 text-2xl font-semibold text-slate-950">{volume.title}</h3>
                        <p className="mt-3 text-sm leading-7 text-indigo-600">核心事件：{volume.coreEvent}</p>
                        <p className="mt-3 text-sm leading-8 text-slate-700 whitespace-pre-wrap break-words">{volume.summary}</p>
                      </div>
                      <div className="grid gap-3 text-sm text-slate-600 lg:min-w-[240px]">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">章节范围</div>
                          <div className="mt-2 text-base font-semibold text-slate-900">{volume.chapterRangeLabel}</div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">卷内章数</div>
                          <div className="mt-2 text-base font-semibold text-slate-900">{volume.chapters.length} 章</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {volume.chapters.map((chapter) => (
                        <span key={`${volume.number}-${chapter.index}`} className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs text-indigo-700">
                          第{chapter.index}章 {chapter.title}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 grid gap-3 xl:grid-cols-2">
                      {volume.chapterOutlines.map((chapter) => (
                        <div key={chapter.index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-400">第{chapter.index}章</span>
                            <span className="font-medium text-slate-800 text-sm">{chapter.title}</span>
                          </div>
                          <p className="text-sm text-slate-600 leading-7 whitespace-pre-wrap break-words">{chapter.summary}</p>
                          {chapter.keyPoints.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {chapter.keyPoints.map((point) => (
                                <span key={point} className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-700">
                                  {point}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* 操作按钮 */}
              <div className="grid gap-4 md:grid-cols-2">
                <button
                  type="button"
                  onClick={handleOpenResultHistory}
                  disabled={!resultRedirectTarget}
                  className="flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-medium text-white transition-all hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <BookOpen className="w-5 h-5" />
                  查看历史详情
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 font-medium text-slate-600 transition-all hover:bg-slate-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  重新开始
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
